import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { pushTopic } from '../../services/adminApiService';
import { MediaUpload } from './MediaUpload';
import './PushTopicForm.css';

interface PushTopicFormProps {
  botId: string;
}

const MAX_TOPIC_LENGTH = 4000;
const MAX_REFERENCE_CONTEXT_LENGTH = 5000;
const TEST_USER_ID = 'U233b2500b6459dac89ba1617f249be19'; // Default test user ID

const PushTopicForm: React.FC<PushTopicFormProps> = ({ botId }) => {
  const { t } = useTranslation();
  const DEFAULT_IMAGE_INSTRUCTION = t('pushTopic.defaultImageInstruction');

  // Set default user ID for test environment
  const environment = process.env.REACT_APP_ENVIRONMENT;
  const defaultUserId = environment === 'test' ? TEST_USER_ID : '';

  const [userId, setUserId] = useState(defaultUserId);
  const [topicContent, setTopicContent] = useState('');
  const [mediaUrls, setMediaUrls] = useState<{
    original: string;
    preview: string;
    fileName: string;
    fileSize: number;
    pageCount?: number;
    type: 'image' | 'pdf';
  } | null>(null);
  const [referenceContext, setReferenceContext] = useState('');
  const [referenceContextExpiresAt, setReferenceContextExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mediaResetTrigger, setMediaResetTrigger] = useState(0);

  // Automatically populate reference context with default instruction when media is uploaded
  useEffect(() => {
    if (mediaUrls) {
      // If media was just uploaded and reference context is empty or doesn't start with the instruction
      if (!referenceContext || !referenceContext.startsWith('添付している')) {
        // Preserve any existing content by appending it after the default instruction
        const existingContent = referenceContext.trim();
        const instruction = mediaUrls.type === 'pdf'
          ? '添付しているPDFは、'  // "The attached PDF is,"
          : DEFAULT_IMAGE_INSTRUCTION;
        setReferenceContext(existingContent ? instruction + existingContent : instruction);
      }
    } else {
      // If media was removed and reference context starts with default instruction, remove it
      if (referenceContext.startsWith(DEFAULT_IMAGE_INSTRUCTION) || referenceContext.startsWith('添付しているPDFは')) {
        const pdfInstruction = '添付しているPDFは、';
        const instructionToRemove = referenceContext.startsWith(pdfInstruction)
          ? pdfInstruction
          : DEFAULT_IMAGE_INSTRUCTION;
        const contentWithoutInstruction = referenceContext.substring(instructionToRemove.length);
        setReferenceContext(contentWithoutInstruction);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUrls]); // Only run when mediaUrls changes

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Either topic content OR media must be provided
    if (!topicContent.trim() && !mediaUrls) {
      setError(t('pushTopic.errorRequiredEitherTopicOrMedia'));
      return;
    }

    if (topicContent && topicContent.length > MAX_TOPIC_LENGTH) {
      setError(t('pushTopic.errorTooLong', { max: MAX_TOPIC_LENGTH }));
      return;
    }

    if (referenceContext && referenceContext.length > MAX_REFERENCE_CONTEXT_LENGTH) {
      setError(t('pushTopic.referenceContextTooLong', { max: MAX_REFERENCE_CONTEXT_LENGTH }));
      return;
    }

    // Validate media URLs require reference context
    if (mediaUrls && !referenceContext.trim()) {
      setError(mediaUrls.type === 'pdf'
        ? t('pushTopic.pdfRequiresContext')
        : t('pushTopic.imageRequiresContext')
      );
      return;
    }

    // Validate media URL lengths
    if (mediaUrls) {
      if (mediaUrls.original.length > 2000) {
        setError(mediaUrls.type === 'pdf'
          ? t('pushTopic.pdfUrlTooLong')
          : t('pushTopic.originalImageUrlTooLong')
        );
        return;
      }
      if (mediaUrls.preview.length > 2000) {
        setError(t('pushTopic.previewImageUrlTooLong'));
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Generate topic ID automatically using timestamp
      const autoTopicId = `topic_${Date.now()}`;

      // Convert datetime-local to ISO 8601 if provided
      const expiresAt = referenceContextExpiresAt
        ? new Date(referenceContextExpiresAt).toISOString()
        : undefined;

      const response = await pushTopic(
        botId,
        userId.trim() || undefined,
        topicContent.trim() || undefined,
        autoTopicId,
        referenceContext.trim() || undefined,
        expiresAt,
        mediaUrls?.type === 'image' ? mediaUrls.original : undefined,
        mediaUrls?.type === 'image' ? mediaUrls.preview : undefined,
        mediaUrls?.type === 'pdf' ? mediaUrls.original : undefined,
        mediaUrls?.type === 'pdf' ? mediaUrls.preview : undefined,
        mediaUrls?.type === 'pdf' ? mediaUrls.fileName : undefined,
        mediaUrls?.type === 'pdf' ? mediaUrls.fileSize : undefined,
        mediaUrls?.type === 'pdf' ? mediaUrls.pageCount : undefined
      );

      setSuccess(t('pushTopic.successMessage', { message: response.message }));

      // Reset form
      setUserId(defaultUserId);
      setTopicContent('');
      setMediaUrls(null);
      setReferenceContext('');
      setReferenceContextExpiresAt('');
      setMediaResetTrigger(prev => prev + 1); // Trigger media upload reset
    } catch (err) {
      console.error('Push topic error:', err);
      setError(err instanceof Error ? err.message : 'Failed to push topic. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUserId(defaultUserId);
    setTopicContent('');
    setMediaUrls(null);
    setReferenceContext('');
    setReferenceContextExpiresAt('');
    setError(null);
    setSuccess(null);
    setMediaResetTrigger(prev => prev + 1); // Trigger media upload reset
  };

  return (
    <form className="push-topic-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="userId" className="form-label">
          {t('pushTopic.userId')} <span className="optional">{t('pushTopic.optional')}</span>
        </label>
        <input
          id="userId"
          type="text"
          className="form-input"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder={t('pushTopic.userIdPlaceholder')}
          disabled={loading}
        />
        <p className="form-hint">{t('pushTopic.userIdHint')}</p>
      </div>

      <div className="form-group">
        <label htmlFor="topicContent" className="form-label">
          {t('pushTopic.topicContent')} <span className="optional">{t('pushTopic.optional')}</span>
        </label>
        <textarea
          id="topicContent"
          className="form-textarea"
          value={topicContent}
          onChange={(e) => setTopicContent(e.target.value)}
          placeholder={t('pushTopic.topicContentPlaceholder')}
          rows={6}
          maxLength={MAX_TOPIC_LENGTH}
          disabled={loading}
        />
        <div className="form-hint-row">
          <p className="form-hint">{t('pushTopic.topicContentHintOptional')}</p>
          <p className={`character-count ${topicContent.length > MAX_TOPIC_LENGTH * 0.9 ? 'warning' : ''}`}>
            {topicContent.length} / {MAX_TOPIC_LENGTH}
          </p>
        </div>
      </div>

      {/* Media Upload feature (images and PDFs) */}
      <MediaUpload
        botId={botId}
        onMediaUrlsChange={setMediaUrls}
        disabled={loading}
        resetTrigger={mediaResetTrigger}
      />

      {/* Info when both media and topic content are provided */}
      {mediaUrls && topicContent.trim() && (
        <div className="alert alert-info">
          ℹ️ {mediaUrls.type === 'pdf'
            ? t('pushTopic.pdfAndTextInfo')
            : t('pushTopic.imageAndTextInfo')
          }
        </div>
      )}

      {/* Reference Context feature */}
      <div className="form-group">
        <label htmlFor="referenceContext" className="form-label">
          {t('pushTopic.referenceContext')} <span className="optional">{t('pushTopic.optional')}</span>
        </label>
        <textarea
          id="referenceContext"
          className="form-textarea"
          value={referenceContext}
          onChange={(e) => setReferenceContext(e.target.value)}
          placeholder={t('pushTopic.referenceContextPlaceholder')}
          rows={8}
          disabled={loading}
        />
        <p className="form-hint">{t('pushTopic.referenceContextHint')}</p>
        <div className="form-hint-row">
          <span></span>
          <p className={`character-count ${referenceContext.length > MAX_REFERENCE_CONTEXT_LENGTH * 0.9 ? 'warning' : ''}`}>
            {referenceContext.length} / {MAX_REFERENCE_CONTEXT_LENGTH}
            {referenceContext.length > MAX_REFERENCE_CONTEXT_LENGTH * 0.9 && (
              <span className="warning-text"> ⚠️ {t('pushTopic.approachingLimit')}</span>
            )}
          </p>
        </div>
        {referenceContext.length > 0 && (
          <>
            <div className="info-box">
              {t('pushTopic.referenceContextInfo')}
            </div>
            <div className="example-hint">
              {t('pushTopic.referenceContextExample')}
            </div>
          </>
        )}
      </div>

      {referenceContext && (
        <div className="form-group">
          <label htmlFor="referenceContextExpiresAt" className="form-label">
            {t('pushTopic.referenceContextExpiresAt')} <span className="optional">{t('pushTopic.optional')}</span>
          </label>
          <input
            type="datetime-local"
            id="referenceContextExpiresAt"
            className="form-input"
            value={referenceContextExpiresAt}
            onChange={(e) => setReferenceContextExpiresAt(e.target.value)}
            placeholder={t('pushTopic.referenceContextExpiresAtPlaceholder')}
            disabled={loading}
          />
          <p className="form-hint">{t('pushTopic.referenceContextExpiresAtHint')}</p>
          <div className="info-box">
            {t('pushTopic.referenceContextExpiresAtInfo')}
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          className="button button-secondary"
          onClick={handleReset}
          disabled={loading}
        >
          {t('pushTopic.resetButton')}
        </button>
        <button
          type="submit"
          className="button button-primary"
          disabled={loading}
        >
          {loading ? t('pushTopic.submitting') : t('pushTopic.submitButton')}
        </button>
      </div>
    </form>
  );
};

export default PushTopicForm;
